import { storeToRefs } from 'pinia';
import { computed } from 'vue';

import { useUserStore } from '~/store/user';

/** Every surface a reseller can switch off for one of its Child Accounts (ENG-5850). */
export type VisibilityKey =
  | 'showFinance'
  | 'showInvoices'
  | 'showBillingSettings'
  | 'showAccountDashboard'
  | 'showChildAccountDashboard'
  | 'showPolicies'
  | 'showPolicyShippingPricing'
  | 'showPolicyBilling'
  | 'showPolicyInvoices'
  | 'showPolicyPayouts'
  | 'showPolicyCoverageMatrix'
  | 'showPolicyWindows'
  | 'showPolicyWidget';

/**
 * Which surfaces the signed-in user may see (ENG-5850).
 *
 * Resolved by identity-svc and carried on `/v1/auth/userinfo`, so this reads the user store rather
 * than fetching. Polarity is visibility: on = the surface renders. Anything unknown resolves to
 * visible — an account with no settings (i.e. everyone today) and a still-loading user both behave
 * exactly as before.
 *
 * Two scopes in one place: `show*` navigation keys remove whole destinations, `showPolicy*` keys
 * remove individual cards from Settings → Policies.
 */
export const useFinanceVisibility = () => {
  const { user } = storeToRefs(useUserStore());

  const visibility = computed(() => user.value?.visibility);

  /** A surface is hidden only on an explicit `false`. */
  const canSee = (surface: VisibilityKey) => visibility.value?.[surface] !== false;

  const showFinance = computed(() => canSee('showFinance'));
  const showPolicies = computed(() => canSee('showPolicies'));

  return {
    /** One card on Settings → Policies. Every block is also gated by the Policies tab itself. */
    canSeePolicyBlock: (block: VisibilityKey) => computed(() => showPolicies.value && canSee(block)),

    /** Parent-level rollup dashboard in the Analytics group. */
    showAccountDashboard: computed(() => canSee('showAccountDashboard')),

    /** The Billing block on Settings → Claims. */
    showBillingSettings: computed(() => canSee('showBillingSettings')),

    /** Child-level dashboard in the Analytics group. */
    showChildAccountDashboard: computed(() => canSee('showChildAccountDashboard')),

    /** The whole Finance nav group — Payouts and Payments. Off removes the group; it never renders empty. */
    showFinance,

    /**
     * The Invoices sub-tab of the Payments page. Depends on Finance: with Finance off the Payments
     * page is unreachable, so this can only ever be true while Finance is.
     */
    showInvoices: computed(() => showFinance.value && canSee('showInvoices')),

    /** The Policies tab itself. Off hides the tab and every block below it. */
    showPolicies,
  };
};
